import { z } from "zod";
/**
 * How a platform nook renders in the detail panel (block 43).
 *
 * Ids, never URLs: building an iframe URL is presentation, and the webapp owns
 * it (override 5).
 */
const indices = z.tuple([z.number(), z.number()]);
const entity = z.object({ indices });
const link = entity.extend({ url: z.string(), display_url: z.string(), expanded_url: z.string() });
const user = z.object({
    id_str: z.string(),
    name: z.string(),
    screen_name: z.string(),
    profile_image_url_https: z.string(),
    profile_image_shape: z.enum(["Circle", "Square", "Hexagon"]),
    verified: z.boolean(),
    verified_type: z.enum(["Business", "Government"]).optional(),
    is_blue_verified: z.boolean(),
    highlighted_label: z
        .object({ description: z.string().optional(), badge: z.object({ url: z.string() }).optional() })
        .optional(),
});
const media = z.object({
    type: z.enum(["photo", "video", "animated_gif"]),
    media_url_https: z.string(),
    original_info: z.object({ width: z.number(), height: z.number() }),
    ext_alt_text: z.string().optional(),
    video_info: z
        .object({
        aspect_ratio: indices,
        variants: z.array(z.object({
            bitrate: z.number().optional(),
            content_type: z.enum(["video/mp4", "application/x-mpegURL"]),
            url: z.string(),
        })),
    })
        .optional(),
});
const tweetBase = z.object({
    id_str: z.string(),
    lang: z.string(),
    created_at: z.string(),
    text: z.string(),
    display_text_range: indices,
    entities: z
        .object({
        hashtags: z.array(entity.extend({ text: z.string() })).optional(),
        symbols: z.array(entity.extend({ text: z.string() })).optional(),
        urls: z.array(link).optional(),
        media: z.array(link).optional(),
        user_mentions: z
            .array(entity.extend({ id_str: z.string(), name: z.string(), screen_name: z.string() }))
            .optional(),
    })
        .optional(),
    user,
    mediaDetails: z.array(media).optional(),
    favorite_count: z.number(),
    note_tweet: z.object({ id: z.string() }).optional(),
});
/**
 * The fields of a `tweet-result` response that react-tweet's `EmbeddedTweet`
 * reads, and no others (override 4). `x.ts` stores its output, so what is in
 * `platform_metadata.tweet` is exactly this.
 */
export const tweetSnapshotSchema = tweetBase.extend({
    conversation_count: z.number(),
    in_reply_to_screen_name: z.string().optional(),
    in_reply_to_status_id_str: z.string().optional(),
    quoted_tweet: tweetBase.optional(),
});
export const nookEmbedSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("x"), tweet: tweetSnapshotSchema }),
    z.object({
        kind: z.literal("youtube"),
        videoId: z.string(),
        start: z.number().optional(),
        vertical: z.boolean(),
    }),
    z.object({ kind: z.literal("instagram"), code: z.string(), reel: z.boolean() }),
    z.object({ kind: z.literal("linkedin"), urn: z.string() }),
    z.object({ kind: z.literal("reddit"), subreddit: z.string(), postId: z.string() }),
    z.object({ kind: z.literal("tiktok"), videoId: z.string() }),
    z.object({ kind: z.literal("facebook"), permalink: z.string(), video: z.boolean() }),
    z.object({ kind: z.literal("threads"), username: z.string(), code: z.string() }),
    /** A product as it was at the save: `priceAt` is when the price was read, never now. */
    z.object({
        kind: z.literal("product"),
        title: z.string(),
        brand: z.string().optional(),
        image: z.string().optional(),
        price: z.number().optional(),
        currency: z.string().optional(),
        priceAt: z.string(),
        /** Out of stock at the save is why `price` can be missing. */
        inStock: z.boolean().optional(),
        rating: z.number().optional(),
        reviewsCount: z.number().optional(),
    }),
]);
//# sourceMappingURL=embed.js.map